import { Link } from 'react-router-dom';
import { QUESTION_BY_SLUG } from '../seo/questions';
import { LANGUAGE_BY_SLUG } from '../seo/languages';

/**
 * /<question-slug> — a landing page that answers one search question directly
 * ("What language is this audio?"). Content comes from src/seo/questions.js;
 * the head tags come from the matching route in src/seo/routes.js (rendered
 * by the caller via SeoHelmet). Reuses the language-page styles.
 */
function QuestionPage({ slug }) {
  const page = QUESTION_BY_SLUG[slug];
  if (!page) return null;
  const languages = page.languages.map((s) => LANGUAGE_BY_SLUG[s]).filter(Boolean);
  const other = Object.values(QUESTION_BY_SLUG).filter((q) => q.slug !== slug);

  const micCta = <Link to="/" className="language-cta-btn">Tap the mic and find out →</Link>;
  const uploadCta = <Link to="/#upload" className="language-cta-btn">Upload a recording →</Link>;

  return (
    <div className="page-container language-page question-page">
      <nav className="breadcrumbs" aria-label="Breadcrumb">
        <Link to="/">LangShazam</Link>
        <span aria-hidden> / </span>
        <span>{page.question}</span>
      </nav>

      <header className="language-hero">
        <h1>{page.question}</h1>
        <p className="language-hero-lede">{page.lede}</p>
        {page.primary === 'upload' ? uploadCta : micCta}
        <p className="language-hero-alt">
          {page.primary === 'upload'
            ? <>Hearing it right now? <Link to="/">Tap the mic instead</Link> and play it near your device.</>
            : <>Have a recording instead? <Link to="/#upload">Upload the audio or video file</Link> — the first 10 minutes are analyzed.</>}
        </p>
      </header>

      <section className="content-card language-section">
        <h2>The short answer</h2>
        <p>{page.answer}</p>
      </section>

      <section className="language-section">
        <h2>How to identify the language</h2>
        <ol className="language-steps">
          {page.steps.map((step) => (
            <li key={step.name}><strong>{step.name}.</strong> {step.text}</li>
          ))}
        </ol>
      </section>

      <section className="language-section">
        <h2>Works for whatever you're hearing</h2>
        <div className="use-case-grid">
          {page.situations.map((s) => (
            <div key={s.title} className="use-case-card">
              <h3>{s.title}</h3>
              <p>{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="language-section">
        <h2>Why it can tell similar languages apart</h2>
        <p>
          Text-based detectors guess from written words, so languages that share vocabulary or a script
          get confused. LangShazam streams the audio to a real-time multilingual speech model that
          identifies the language from the sound itself — rhythm, vowels, intonation — and tags every
          phrase with its language and confidence. That is how it separates Spanish from Portuguese,
          Hindi from Urdu, or Norwegian from Danish.
        </p>
      </section>

      <section className="language-section faq-section">
        <h2>Questions</h2>
        <div className="faq-list">
          {page.faq.map((item) => (
            <details key={item.q} className="faq-item">
              <summary>{item.q}</summary>
              <p>{item.a}</p>
            </details>
          ))}
        </div>
      </section>

      {languages.length > 0 && (
        <section className="language-section">
          <h2>Think you know which language it is?</h2>
          <div className="lang-chips language-related">
            {languages.map((l) => (
              <Link key={l.slug} to={`/languages/${l.slug}`} className="lang-chip">{l.name}</Link>
            ))}
            <Link to="/languages" className="lang-chip lang-chip-more">All 32 languages →</Link>
          </div>
        </section>
      )}

      <section className="language-section language-final-cta">
        <h2>Find out in a few seconds</h2>
        <p>Free, works in any browser on desktop or mobile, no app to install.</p>
        {page.primary === 'upload' ? uploadCta : micCta}
        {other.map((q) => (
          <p key={q.slug} className="language-hero-alt">
            Also: <Link to={`/${q.slug}`}>{q.question}</Link>
          </p>
        ))}
      </section>
    </div>
  );
}

export default QuestionPage;
